import { useEffect } from "react";
import { useLockStore } from "../../store/lockStore";
import { useAuth } from "../../auth/AuthProvider";
import { lockApi } from "../../api/lockApi";
import { asError, eventBus } from "@optimyl/events";

const RETRY_INTERVAL_MS = 3000;

/**
 * Defensive retry for the case where the lock screen ends up displaying the
 * caller's own email. With Path 4 of AcquireLockScript now unconditional for
 * same-email/different-session, the new device's first acquireLock in
 * useLockLifecycle reclaims immediately — the lock screen should never show
 * the user's own email under normal flow. This hook remains as belt-and-
 * suspenders: if any edge case produces that state (e.g. SignalR push arriving
 * before the initial acquire response), the retry recovers within ~3s rather
 * than leaving the user stuck.
 *
 * Polls acquireLock every 3s ONLY when (lockStatus === "locked-by-other" AND
 * lockedBy matches the current user's email). Stops once the user reclaims or
 * once a different user legitimately becomes the holder.
 *
 * Email matching is case-insensitive — identity providers don't promise
 * consistent casing.
 */
export function useSelfReclaimRetry(
  enabled: boolean,
  formId: string | null,
  sessionId: string,
  lockStatus: string,
  lockedBy: string | null,
): void {
  const { user } = useAuth();
  const myEmail = user?.profile.email as string | undefined;
  const setLockStatus = useLockStore((s) => s.setLockStatus);
  const setRequestInProgress = useLockStore((s) => s.setRequestInProgress);

  useEffect(() => {
    if (!enabled || !formId) return;
    if (lockStatus !== "locked-by-other") return;
    if (!myEmail || !lockedBy) return;
    if (lockedBy.toLowerCase() !== myEmail.toLowerCase()) return;

    const id = window.setInterval(async () => {
      try {
        const result = await lockApi.acquireLock(formId, sessionId);
        if (result.acquired) {
          setLockStatus("locked");
          setRequestInProgress(result.requestAccessBy);
        } else if (result.lockedBy && result.lockedBy.toLowerCase() !== myEmail.toLowerCase()) {
          // Someone else legitimately holds it now (e.g. the lock-screen
          // viewer took over while we were trying to reclaim). Update the UI
          // so we stop showing the user their own email.
          setLockStatus("locked-by-other", result.lockedBy);
        }
        // Otherwise we're still seeing self — keep retrying. The interval
        // will be torn down by the effect cleanup once lockStatus / lockedBy
        // changes enough to fail one of the guards above.
      } catch (err) {
        eventBus.publish("error", { error: asError(err), context: "useSelfReclaimRetry" });
      }
    }, RETRY_INTERVAL_MS);

    return () => window.clearInterval(id);
  }, [enabled, formId, sessionId, lockStatus, lockedBy, myEmail, setLockStatus, setRequestInProgress]);
}
